import { isArray } from 'class-validator';

import { isObject } from '../assertFunctions';
import { jsonObjectClone } from '../jsonClone/jsonClone';

/** Transform arbitrarily nested objects that resolve to primitives into
 * a flattened structure.
 *
 * Arrays will not be traversed and will be cloned and assigned to the
 * current property directly.
 * Better generic type inference might be implemented later.*/
export const flatten = <T>(obj: Record<string, unknown>): Record<string, T> => {
  const keyVals = new Map<string, T>();
  const joinKeys = (key: string, prefix?: string) =>
    prefix ? [prefix, key].join('.') : key;
  const extractFromEntries = (
    obj: Record<string, unknown>,
    prefix?: string,
  ) => {
    const entries = Object.entries(obj);
    for (const [key, value] of entries) {
      if (isArray(value)) {
        keyVals.set(joinKeys(key, prefix), jsonObjectClone(value) as T);
        continue;
      }
      if (!isObject(value)) {
        keyVals.set(joinKeys(key, prefix), value as T);
        continue;
      }
      extractFromEntries(value, joinKeys(key, prefix));
    }
  };
  extractFromEntries(obj);
  return Object.fromEntries(keyVals.entries());
};

export type NestedRecord<T> = {
  [key: string]: T | NestedRecord<T>;
};

/** Transforms a flattened object back to its original Record
 * nested value.*/
export const unflatten = <T = unknown>(
  flat: Record<string, T>,
): NestedRecord<T> => {
  const result: Record<string, T> = {};

  for (const [flatKey, value] of Object.entries(flat)) {
    const keys = flatKey.split('.');
    let current: NestedRecord<T> = result;

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const isLast = i === keys.length - 1;

      if (isLast) {
        if (isArray(value)) {
          current[key] = [...value] as T;
        } else {
          current[key] = value;
        }
      } else {
        if (!isObject(current[key])) {
          current[key] = {};
        }
        current = current[key];
      }
    }
  }

  return result;
};
